import React from 'react'
import { Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { TStorageItemRS, TModuleOptions } from '../../../../models/storageModel'
import { getInventoryValuation, TInventoryValuationRow } from '../../../../utils/reportUtils'
import { TExcelColumn } from '../../../../utils/excelExport'
import { textNumberFormater } from '../../../../utils'

const { Text } = Typography

interface InventoryValuationReportProps {
  data: TStorageItemRS[]
  categoryOptions: TModuleOptions[]
  loading?: boolean
}

export const valuationExcelColumns: TExcelColumn[] = [
  { header: 'Código', key: 'code', width: 18 },
  { header: 'Producto', key: 'name', width: 30 },
  { header: 'Categoría', key: 'category', width: 22 },
  { header: 'Existencia', key: 'totalQuantity', width: 14, format: 'number' },
  { header: 'Precio Compra Unit.', key: 'purchasePrice', width: 18, format: 'money' },
  { header: 'Precio Venta Unit.', key: 'sellPrice', width: 18, format: 'money' },
  { header: 'Costo Total (Stock × Compra)', key: 'totalCost', width: 22, format: 'money' },
  { header: 'Valor Venta Total (Stock × Venta)', key: 'totalSell', width: 22, format: 'money' },
  { header: 'Margen Estimado (%)', key: 'estimatedMargin', width: 18, format: 'percent' }
]

export function getValuationExcelData(data: TStorageItemRS[], categoryOptions: TModuleOptions[]) {
  return getInventoryValuation(data, categoryOptions)
}

export const InventoryValuationReport: React.FC<InventoryValuationReportProps> = ({
  data,
  categoryOptions,
  loading = false
}) => {
  const valuationData = getInventoryValuation(data, categoryOptions)

  const totalCostSum = valuationData.reduce((acc, row) => acc + row.totalCost, 0)
  const totalSellSum = valuationData.reduce((acc, row) => acc + row.totalSell, 0)

  const columns: ColumnsType<TInventoryValuationRow> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Código',
      dataIndex: 'code',
      key: 'code',
      width: 130,
      render: (text) => <code>{text || '-'}</code>
    },
    {
      title: 'Producto',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Categoría',
      dataIndex: 'category',
      key: 'category',
      width: 150
    },
    {
      title: 'Existencia',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      width: 110,
      align: 'right',
      render: (val) => textNumberFormater(val)
    },
    {
      title: 'Precio Compra',
      dataIndex: 'purchasePrice',
      key: 'purchasePrice',
      width: 130,
      align: 'right',
      render: (val) => `$ ${textNumberFormater(val)}`
    },
    {
      title: 'Precio Venta',
      dataIndex: 'sellPrice',
      key: 'sellPrice',
      width: 130,
      align: 'right',
      render: (val) => `$ ${textNumberFormater(val)}`
    },
    {
      title: 'Costo Total Stock',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 160,
      align: 'right',
      sorter: (a, b) => a.totalCost - b.totalCost,
      render: (val) => <Text strong style={{ color: '#1890ff' }}>$ {textNumberFormater(val)}</Text>
    },
    {
      title: 'Valor Venta Total',
      dataIndex: 'totalSell',
      key: 'totalSell',
      width: 160,
      align: 'right',
      sorter: (a, b) => a.totalSell - b.totalSell,
      render: (val) => <Text strong style={{ color: '#52c41a' }}>$ {textNumberFormater(val)}</Text>
    },
    {
      title: 'Margen Est.',
      dataIndex: 'estimatedMargin',
      key: 'estimatedMargin',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.estimatedMargin - b.estimatedMargin,
      render: (margin) => (
        <Tag color={margin >= 30 ? 'green' : margin > 0 ? 'blue' : 'red'}>
          {margin}%
        </Tag>
      )
    }
  ]

  return (
    <div>
      <div style={{ padding: '12px 16px', background: '#f5f5f5', borderRadius: 8, marginBottom: 16, display: 'flex', gap: 24 }}>
        <div>
          <Text type="secondary">Costo Total Inventario: </Text>
          <Text strong style={{ fontSize: '1.1rem', color: '#1890ff' }}>
            $ {textNumberFormater(totalCostSum)}
          </Text>
        </div>
        <div>
          <Text type="secondary">Valor Potencial Venta: </Text>
          <Text strong style={{ fontSize: '1.1rem', color: '#52c41a' }}>
            $ {textNumberFormater(totalSellSum)}
          </Text>
        </div>
        <div>
          <Text type="secondary">Ganancia Bruta Estimada: </Text>
          <Text strong style={{ fontSize: '1.1rem', color: '#722ed1' }}>
            $ {textNumberFormater(totalSellSum - totalCostSum)}
          </Text>
        </div>
      </div>

      <div style={{ overflowX: 'auto', width: '100%' }}>
        <Table<TInventoryValuationRow>
          columns={columns}
          dataSource={valuationData}
          loading={loading}
          pagination={{
            defaultPageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['15', '30', '50', '100'],
            showTotal: (total) => `Total ${total} productos valorizados`
          }}
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
        />
      </div>
    </div>
  )
}

export default InventoryValuationReport
